// A D1-shaped database - `prepare(sql).bind(...).first/all/run()` and
// `batch([...])` - so the query layer copied from kadret works unchanged. The
// statements themselves are plain data: an `Executor` runs them, which is the
// sqlite worker in the app (./client.ts) and node:sqlite in the tests.

export type SqlValue = string | number | null;

export type Request =
  | { kind: 'first' | 'all' | 'run', sql: string, params: SqlValue[] }
  | { kind: 'batch', statements: { sql: string, params: SqlValue[] }[] }
  // several statements in one transaction, for the schema
  | { kind: 'script', sql: string };

export type RunResult = { meta: { changes: number } };

export type Executor = (request: Request) => Promise<unknown>;

export interface Statement {
  readonly sql: string;
  readonly params: SqlValue[];
  first<T>(): Promise<T | null>;
  all<T>(): Promise<{ results: T[] }>;
  run(): Promise<RunResult>;
}

export interface Database {
  prepare(sql: string): {
    bind(...values: (SqlValue | boolean)[]): Statement,
  };
  batch(statements: Statement[]): Promise<RunResult[]>;
  script(sql: string): Promise<void>;
}

// D1 binds booleans as 1 and 0; sqlite itself refuses them
const sqlValue = (value: SqlValue | boolean): SqlValue =>
  typeof value === 'boolean' ? Number(value) : value;

export const makeDatabase = (execute: Executor): Database => ({
  prepare: sql => ({
    bind: (...values) => {
      const params = values.map(sqlValue);
      return {
        sql,
        params,
        first: <T>() =>
          execute({ kind: 'first', sql, params }) as Promise<T | null>,
        all: <T>() =>
          execute({ kind: 'all', sql, params }) as Promise<{ results: T[] }>,
        run: () => execute({ kind: 'run', sql, params }) as Promise<RunResult>,
      };
    },
  }),
  batch: statements =>
    execute({
      kind: 'batch',
      statements: statements.map(({ sql, params }) => ({ sql, params })),
    }) as Promise<RunResult[]>,
  script: async sql => {
    await execute({ kind: 'script', sql });
  },
});
